/**
 * A runtime lot as an app shape.
 *
 * The boundary half of what was `utils/lot-projection.js` in the mobile portal.
 * That module named its own seam — two of its four functions turn a lot into a
 * shape another store consumes, and two produce a human-readable label — and
 * this is the first half arriving where it belongs. The label half stayed with
 * the surface that draws a lane header.
 *
 * Both of these are called from the bidding store's data half: `toggleLike`
 * hands a card to the liked store, and `seedVehicleDetail` primes the asset
 * cache so tapping a lot paints instantly instead of waiting on a fetch.
 */

// Rough urgency for the My Vehicles page, in hours. A lot already running is
// imminent; a scheduled one spreads across the week by its day.
const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const planEtaHrs = (lot) => {
    if (!lot.scheduled) return 1;
    const index = DAY_ORDER.indexOf(String(lot.scheduled).slice(0, 3));
    return 24 + Math.max(0, index) * 24;
};

/** A lot as a liked asset card. */
export const assetCardForLot = (lot) => ({
    id: lot.assetId,
    title: `${lot.year} ${lot.make} ${lot.model}`,
    year: lot.year,
    make: lot.make,
    model: lot.model,
    trim: lot.trim,
    image: lot.imageUrl,
    price: lot.askingPrice || lot.currentBid || null,
    auctionInHrs: planEtaHrs(lot),
    lights: Array.isArray(lot.lights) ? lot.lights.filter(Boolean) : [],
    light: Array.isArray(lot.lights) && lot.lights.length ? lot.lights[0] : null,
});

/**
 * A lot as the uppercase document the asset detail page renders from, so
 * tapping a lot paints instantly instead of waiting on a fetch for a record the
 * bidding store is already holding.
 */
export const detailDocForLot = (lot) => ({
    _id: lot.assetId,
    YEAR: lot.year,
    MAKE: lot.make,
    MODEL: lot.model,
    SERIES: lot.trim ?? null,
    BOOKVALUE: lot.mmr != null ? String(lot.mmr) : null,
    AUCTIONID: lot.sale ?? null,
    IMAGES: lot.imageUrl ? [{ URL: lot.imageUrl, POSITION: 0 }] : [],
    SALELIGHTS: Array.isArray(lot.lights)
      ? lot.lights.filter(Boolean).map((code) => ({ NAME: String(code), TITLE: String(code), COLOR: String(code) }))
      : [],
});
